import React, { useState } from "react";
import { Link } from "react-router-dom";
import { custApi, CUST_USER_KEY } from "../../lib/customerApi";
import { useCustAuth } from "../../lib/customerAuth";
import {
  ShieldWarning, ChatCircleDots, ArrowClockwise, Phone, CheckCircle, XCircle,
} from "@phosphor-icons/react";

/**
 * Full-screen lock displayed when a pending / rejected customer opens a
 * gated module (Hygiene). Health and other pages stay accessible.
 * Customer can:
 *   • Refresh status (re-fetches /api/me, updates local storage)
 *   • Open chat with Rigo (customer-care)
 *   • Tap the office phone number to call
 */
export default function MeVerificationGate({ moduleLabel = "Hygiene" }) {
  const { customer } = useCustAuth();
  const status = (customer?.status || "").toLowerCase();
  const isRejected = status === "rejected";
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // The auth context stores the customer snapshot in localStorage under
  // CUST_USER_KEY. We update that directly + force a reload so the whole
  // app picks up the fresh status (bottom-nav badge, gate outcome, etc.).
  const refresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      const r = await custApi.get("/me");
      const fresh = r.data || {};
      const merged = { ...(customer || {}), ...fresh };
      localStorage.setItem(CUST_USER_KEY, JSON.stringify(merged));
      // If active, reload to fall through the gate.
      if ((fresh.status || "").toLowerCase() === "active") {
        window.location.reload();
        return;
      }
      setError(
        (fresh.status || "").toLowerCase() === "rejected"
          ? "Your account was not approved."
          : "Still under review — please check back a little later."
      );
    } catch {
      setError("Couldn’t refresh right now. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const officePhone = "+91 8547 000 111"; // display-only; free-form CTA

  return (
    <div className="font-body pb-10 px-5 pt-6" data-testid="me-verification-gate">
      <div
        className={`rounded-3xl overflow-hidden border shadow-[0_10px_40px_rgba(0,0,0,0.05)] ${
          isRejected ? "border-[#F4C7B9]" : "border-[#F9E4B7]"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 pt-6 pb-8 text-white ${
            isRejected
              ? "bg-gradient-to-br from-[#DC2626] to-[#7F1D1D]"
              : "bg-gradient-to-br from-[#7A5A11] via-[#B58412] to-[#DC7A18]"
          }`}
        >
          <div className="w-16 h-16 rounded-2xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center backdrop-blur-md">
            {isRejected ? (
              <XCircle size={30} weight="fill" />
            ) : (
              <ShieldWarning size={30} weight="fill" />
            )}
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.22em] font-bold opacity-85">
            {isRejected ? "Account not approved" : "Verification pending"}
          </div>
          <div className="font-display font-black text-[24px] leading-[1.15] mt-1">
            {isRejected ? (
              <>
                We couldn’t approve<br />your account
              </>
            ) : (
              <>
                {moduleLabel} unlocks<br />once you’re verified
              </>
            )}
          </div>
          <p className="text-[13px] leading-snug mt-3 text-white/85 max-w-[300px]">
            {isRejected
              ? "Please reach out to our team so we can review the details together and help you get on board."
              : `Our team is checking your details. Meanwhile, your Health tools and profile remain fully available — only ${moduleLabel} needs verification.`}
          </p>
        </div>

        {/* Body */}
        <div className="bg-white px-5 py-5">
          {/* Refresh */}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            data-testid="me-verification-refresh"
            className="w-full flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-[13px] font-bold text-[#111827] active:scale-[0.99] transition disabled:opacity-60"
          >
            <ArrowClockwise size={16} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Checking…" : "I’ve been verified — refresh"}
          </button>
          {error && (
            <div className="mt-2 text-[11px] text-[#7A2E18] bg-[#FEF7F6] border border-[#F4C7B9] rounded-lg px-3 py-2 flex items-center gap-2" data-testid="me-verification-error">
              <ShieldWarning size={12} weight="fill" /> {error}
            </div>
          )}

          {/* Contact us — chat */}
          <Link
            to="/me/chat"
            data-testid="me-verification-chat"
            className="mt-3 w-full flex items-center gap-3 rounded-2xl bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white px-4 py-3 active:scale-[0.99] transition shadow-[0_8px_24px_rgba(220,38,38,0.28)]"
          >
            <div className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center">
              <ChatCircleDots size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="font-display font-bold text-[14px]">Chat with our team</div>
              <div className="text-[11px] text-white/85">Rigo replies in a few minutes</div>
            </div>
          </Link>

          {/* Contact us — support ticket */}
          <Link
            to="/me/customer-care"
            data-testid="me-verification-ticket"
            className="mt-2 w-full flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-4 py-3 active:scale-[0.99] transition"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center">
              <Phone size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="font-display font-bold text-[14px] text-[#111827]">Contact customer care</div>
              <div className="text-[11px] text-gray-500">Raise a ticket or call us</div>
            </div>
          </Link>

          {/* What stays available */}
          <div className="mt-5 rounded-2xl bg-[#FEF7F6] border border-[#F4C7B9] px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A2E18] font-bold">
              Available right now
            </div>
            <ul className="mt-2 space-y-1.5">
              {["Health tools & reminders", "Daily Care & Home", "Profile, Wallet & Dues"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-[12px] text-[#111827]">
                  <CheckCircle size={13} weight="fill" className="text-[#15803D] shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
